import Button from '@/app/components/common/Button'
import Title from '@/app/components/common/Title'
import { emitFindHotelsPopularDestGAEvent } from '@/app/ga-event-helpers'
import { getLocaleByCode } from '@/app/utils'
import cn from 'classnames'
import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import { FC } from 'react'

interface DestinationCardProps {
	destination: string
	image: string
	startDate: string
	endDate: string
	price: number
	url: string
	placeId: number
	currencySymbol?: string
	lang: string
}

const DestinationCard: FC<DestinationCardProps> = ({
	destination,
	image,
	startDate,
	endDate,
	price,
	url,
	placeId,
	lang,
	currencySymbol = '$',
}) => {
	const locale = getLocaleByCode(lang as string)
	const router = useRouter()
	const searchParams = useSearchParams()
	const displayStartDate = format(new Date(startDate), 'EEE, d MMM', { locale })
	const displatEndDate = format(new Date(endDate), 'EEE, d MMM', { locale })
	const DEFAULT_ROOM_GUESTS = 2
	const t = useTranslations('Keywords')

	const onFindHotels = () => {
		const newParams = new URLSearchParams()
		newParams.set('check-in-date', startDate)
		newParams.set('check-out-date', endDate)
		newParams.set('room', String(DEFAULT_ROOM_GUESTS))
		newParams.set('locationName', destination)

		if (placeId) {
			newParams.set('placeID', String(placeId))
		}

		searchParams.forEach((value, key) => {
			if (!newParams.has(key) && key !== 'f' && key !== 'b') {
				newParams.set(key, value)
			}
		})

		const queryString = newParams.toString()
		const fullUrl = `${lang}/${url}${queryString ? `?${queryString}` : ''}`

		router.push(fullUrl)
	}

	const trackOnFindHotels = () => {
		emitFindHotelsPopularDestGAEvent(destination)
		setTimeout(() => {
			onFindHotels()
		}, 300)
	}

	return (
		<div className="destination-card">
			<div className="destination-card__image-container relative h-[214px] w-full">
				<Image
					src={image}
					alt={`${destination} destination`}
					fill
					className="object-cover rounded-2xl"
					priority
				/>
			</div>
			<div className="destination-card__content">
				<Title
					level="2"
					testId={`destination-title-${destination}`}
					bold
					className="destination-card__title !text-2xl !leading-7 mb-0.5"
				>
					{destination}
				</Title>
				<p className="destination-card__dates leading-5 text-text-700 mb-3">
					{displayStartDate} - {displatEndDate}
				</p>
				<div className="destination-card__footer flex items-center justify-between">
					<span className="destination-card__price font-semibold text-xl">
						{t('from')} {currencySymbol}
						{price}
					</span>
					<Button
						name="find-hotels-button"
						className={
							cn("!bg-gradient-to-r from-main to-[#E1004B] !rounded-full w-fit px-6 py-3 leading-5 transition-opacity hover:opacity-80",
								{ "whitespace-nowrap": lang !== 'fr' })
						}
						textColor="white"
						font="bold"
						fontSize="base"
						onClick={() => {
							trackOnFindHotels()
						}}
					>
						{t('findHotels')}
					</Button>
				</div>
			</div>
		</div>
	)
}

export default DestinationCard
